import '../styles/fleet-design.css';
import { monogram } from '../fleet/alertMeta';
import { useLockBodyScroll } from '../fleet/useLockBodyScroll';
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FaArrowLeft,
  FaBus,
  FaSearch,
  FaExclamationCircle,
} from 'react-icons/fa';
import { db } from '../firebase';

interface BusCostEstimatePageProps {
  buses: Array<{
    id: string;
    plate: string;
    name: string;
    fuelTankCapacityL?: number | null;
    benchmarkKmPerL?: number | null;
    status: string;
  }>;
  fleetOrgCode: string | null;
  onBack: () => void;
  onDrillDownBus: (busId: string) => void;
  currency?: string;
}

interface RefuelEvent {
  busId: string;
  costEstimate: number;
  litersAdded: number;
  timestamp: Timestamp;
}

interface AttendanceRecord {
  busId: string;
  distanceKm: number;
}

type Period = 7 | 30 | 90;

const PERIOD_LABELS: Record<Period, string> = {
  7: '7 days',
  30: '30 days',
  90: '90 days',
};

/* Zana's purple ramp, darkest first, with two warm accents at the tail so a
   ten-bus fleet does not become ten indistinguishable purples. Matches
   src/styles/fleet-design.css — change both together. */
const CHART_COLORS = [
  '#3a2a7a', '#5a34c9', '#6f4ff0', '#8a63f0', '#9b7cff',
  '#b49bff', '#c4b3ff', '#d97706', '#16a34a', '#dc2626',
];

function fmt(n: number, currency: string): string {
  return `${currency} ${n.toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;
}

function fmtShort(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(Math.round(v));
}


/* Shared with the loader's tsMs(): busRefuelEvents.timestamp is not one shape in
   the wild — Firestore Timestamp, plain {seconds}, Date, epoch number or ISO
   string all occur. Anything unreadable returns 0, which every caller treats as
   "no usable date" rather than as 1970. */
export function tsMsStatic(t: unknown): number {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t === 'string') { const n = Date.parse(t); return Number.isNaN(n) ? 0 : n; }
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'object') {
    const o = t as { toMillis?: () => number; toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.toDate === 'function') return o.toDate().getTime();
    const s = o.seconds ?? o._seconds;
    if (typeof s === 'number') return s * 1000;
  }
  return 0;
}



export default function BusCostEstimatePage({
  buses,
  fleetOrgCode,
  onBack,
  onDrillDownBus,
  currency = 'KES',
}: BusCostEstimatePageProps) {
  useLockBodyScroll();
  const [period, setPeriod] = useState<Period>(30);
  const [refuelEvents, setRefuelEvents] = useState<RefuelEvent[]>([]);
  const [prevRefuelEvents, setPrevRefuelEvents] = useState<RefuelEvent[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  /* A read that FAILED and a period that genuinely has no spend both used to
     render as "KES 0". They are completely different facts: one means nothing
     was spent, the other means we do not know. Money is exactly where that
     confusion is least affordable. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [railSort, setRailSort] = useState<'cost' | 'perKm' | 'km'>('cost');
  const [selectedBusId, setSelectedBusId] = useState<string | null>(null);

  const busIds = useMemo(() => buses.map((b) => b.id), [buses]);
  /* `buses` is re-created by its parent on every Firestore snapshot — including
     every live-location tick — so depending on the array identity restarted
     this whole load every few seconds. Depend on the CONTENT instead. */
  const busIdsKey = useMemo(() => busIds.join(','), [busIds]);

  const matchesSearch = (r: { name: string; plate: string }) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.name.toLowerCase().includes(q) || r.plate.toLowerCase().includes(q);
  };

  useEffect(() => {
    if (busIds.length === 0) {
      setLoading(false);
      return;
    }

    const chunks: string[][] = [];
    for (let i = 0; i < busIds.length; i += 10) {
      chunks.push(busIds.slice(i, i + 10));
    }

    const currentCutoffMs = Date.now() - period * 24 * 60 * 60 * 1000;
    const prevCutoffMs = Date.now() - period * 2 * 24 * 60 * 60 * 1000;

    /* `busRefuelEvents.timestamp` is not one shape in the wild: the app writes a
       Firestore Timestamp, but older rows and some paths carry a plain
       `{seconds}` object, a Date, an epoch number or an ISO string. The previous
       version returned 0 for everything except a real Timestamp, and 0 is before
       every cutoff — so any row of another shape was dropped from EVERY period
       without a word. Returning NaN-free millis for each known shape, and only
       falling back to 0 when the value is genuinely unreadable. */
    const tsMs = (e: RefuelEvent): number => {
      const t = e.timestamp as unknown;
      if (!t) return 0;
      if (typeof t === 'number') return t;
      if (typeof t === 'string') {
        const parsed = Date.parse(t);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      if (t instanceof Date) return t.getTime();
      if (typeof t === 'object') {
        const obj = t as { toMillis?: () => number; toDate?: () => Date; seconds?: number; _seconds?: number };
        if (typeof obj.toMillis === 'function') return obj.toMillis();
        if (typeof obj.toDate === 'function') return obj.toDate().getTime();
        const secs = obj.seconds ?? obj._seconds;
        if (typeof secs === 'number') return secs * 1000;
      }
      return 0;
    };

    async function load() {
      setLoading(true);
      setLoadError(null);
      let refuelFailed: unknown = null;
      try {
        const allRefuelEvents: RefuelEvent[] = [];
        const attendance: AttendanceRecord[] = [];

        for (const chunk of chunks) {
          /* The two reads are deliberately INDEPENDENT. They used to share one
             try block, so a refused or failing `busAttendance` read threw before
             the refuel events were ever stored — and the page then rendered a
             confident "KES 0" built on data it had actually thrown away. One
             collection failing must not silently zero the other. */
          // No timestamp filter in the query — avoids a composite index. Filtered by date below.
          try {
            const refuelSnap = await getDocs(query(
              collection(db, 'busRefuelEvents'),
              where('busId', 'in', chunk),
            ));
            refuelSnap.forEach((d) => {
              const data = d.data();
              allRefuelEvents.push({
                busId: data.busId as string,
                costEstimate: Number(data.costEstimate ?? data.costKsh ?? data.cost ?? 0),
                litersAdded: Number(data.litersAdded ?? data.litres ?? data.liters ?? 0),
                timestamp: data.timestamp as Timestamp,
              });
            });
          } catch (err) {
            // Never swallow: a silent handler is what hid the missing index in July.
            console.error('[BusCostEstimatePage] refuel read failed', err);
            refuelFailed = err;
          }

          try {
            // Include schoolCode so fleet_admin rules allow the list (busId-only
            // was rejected → 0 km while refuels still showed).
            const attendQ = fleetOrgCode
              ? query(
                  collection(db, 'busAttendance'),
                  where('schoolCode', '==', fleetOrgCode),
                  where('busId', 'in', chunk),
                )
              : query(
                  collection(db, 'busAttendance'),
                  where('busId', 'in', chunk),
                );
            const attendSnap = await getDocs(attendQ);
            attendSnap.forEach((d) => {
              const data = d.data();
              const km = Number(data.distanceKm ?? 0);
              if (km > 0) {
                attendance.push({ busId: data.busId as string, distanceKm: km });
              }
            });
          } catch (err) {
            // Distance only powers cost-per-km. Losing it must not cost us the money.
            console.error('[BusCostEstimatePage] attendance read failed', err);
          }
        }

        // Filter client-side by period
        const currentEvents = allRefuelEvents.filter(e => tsMs(e) >= currentCutoffMs);
        const prevEvents = allRefuelEvents.filter(e => tsMs(e) >= prevCutoffMs && tsMs(e) < currentCutoffMs);

        setRefuelEvents(currentEvents);
        setPrevRefuelEvents(prevEvents);
        setAttendanceRecords(attendance);
        if (refuelFailed) {
          setLoadError('Fuel spend could not be read, so the totals below are incomplete.');
        }
      } catch (err) {
        console.error('[BusCostEstimatePage] load failed', err);
        setLoadError('Fuel spend could not be loaded.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [busIdsKey, period, fleetOrgCode]);

  const perBusCosts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of refuelEvents) {
      map[e.busId] = (map[e.busId] ?? 0) + e.costEstimate;
    }
    return map;
  }, [refuelEvents]);

  const prevPerBusCosts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of prevRefuelEvents) {
      map[e.busId] = (map[e.busId] ?? 0) + e.costEstimate;
    }
    return map;
  }, [prevRefuelEvents]);

  const fleetTotal = useMemo(
    () => Object.values(perBusCosts).reduce((s, v) => s + v, 0),
    [perBusCosts],
  );

  const prevFleetTotal = useMemo(
    () => Object.values(prevPerBusCosts).reduce((s, v) => s + v, 0),
    [prevPerBusCosts],
  );

  const trendPct = useMemo(() => {
    if (prevFleetTotal === 0) return null;
    return ((fleetTotal - prevFleetTotal) / prevFleetTotal) * 100;
  }, [fleetTotal, prevFleetTotal]);

  const sortedBuses = useMemo(
    () => [...buses].sort((a, b) => (perBusCosts[b.id] ?? 0) - (perBusCosts[a.id] ?? 0)),
    [buses, perBusCosts],
  );

  const biggestSpenderId =
    sortedBuses.length > 0 && (perBusCosts[sortedBuses[0].id] ?? 0) > 0
      ? sortedBuses[0].id
      : null;

  const barChartData = useMemo(
    () =>
      sortedBuses
        .filter((b) => (perBusCosts[b.id] ?? 0) > 0)
        .map((b, index) => ({
          name: b.name || b.plate,
          cost: perBusCosts[b.id] ?? 0,
          busId: b.id,
          fill: index === 0 ? '#dc2626' : index === 1 ? '#f97316' : '#16a34a',
        })),
    [sortedBuses, perBusCosts],
  );


  const costPerKmData = useMemo(() => {
    const kmMap: Record<string, number> = {};
    for (const r of attendanceRecords) {
      kmMap[r.busId] = (kmMap[r.busId] ?? 0) + r.distanceKm;
    }
    return buses
      .map((b) => ({
        id: b.id,
        name: b.name || b.plate,
        plate: b.plate,
        totalKm: kmMap[b.id] ?? 0,
        totalCost: perBusCosts[b.id] ?? 0,
        costPerKm:
          (kmMap[b.id] ?? 0) > 0 ? (perBusCosts[b.id] ?? 0) / (kmMap[b.id] ?? 0) : null,
      }))
      .filter((row) => row.totalKm > 0 || row.totalCost > 0)
      .sort((a, b) => (b.costPerKm ?? 0) - (a.costPerKm ?? 0));
  }, [buses, perBusCosts, attendanceRecords]);
  /* ---- per-bus fuel volume, so efficiency can be shown honestly -------- */
  const perBusLitres = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of refuelEvents) map[e.busId] = (map[e.busId] ?? 0) + (e.litersAdded || 0);
    return map;
  }, [refuelEvents]);

  const perBusRefuelCount = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of refuelEvents) map[e.busId] = (map[e.busId] ?? 0) + 1;
    return map;
  }, [refuelEvents]);

  const perBusLastRefuel = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of refuelEvents) {
      const ms = tsMsStatic(e.timestamp);
      if (ms > (map[e.busId] ?? 0)) map[e.busId] = ms;
    }
    return map;
  }, [refuelEvents]);

  const kmByBus = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of attendanceRecords) map[r.busId] = (map[r.busId] ?? 0) + r.distanceKm;
    return map;
  }, [attendanceRecords]);

  const fleetKm = useMemo(() => Object.values(kmByBus).reduce((s, v) => s + v, 0), [kmByBus]);
  const fleetCostPerKm = fleetKm > 0 ? fleetTotal / fleetKm : null;

  /* Cumulative fleet spend, this period against the one before it, aligned by
     day offset so the two lines are comparable. Refuels are lumpy — a daily
     (non-cumulative) line would be mostly zeros with occasional spikes, which
     reads as noise rather than as a trend. */
  const spendCurve = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const startNow = Date.now() - period * dayMs;
    const startPrev = Date.now() - period * 2 * dayMs;
    const bucket = (events: RefuelEvent[], start: number) => {
      const out = new Array(period + 1).fill(0);
      for (const e of events) {
        const ms = tsMsStatic(e.timestamp);
        if (!ms) continue;
        const idx = Math.floor((ms - start) / dayMs);
        if (idx >= 0 && idx <= period) out[idx] += e.costEstimate;
      }
      let run = 0;
      return out.map((v) => (run += v));
    };
    const cur = bucket(refuelEvents, startNow);
    const prev = bucket(prevRefuelEvents, startPrev);
    return cur.map((v, i) => ({
      day: new Date(startNow + i * dayMs).toLocaleDateString([], { day: 'numeric', month: 'short' }),
      current: v,
      previous: prev[i] ?? 0,
    }));
  }, [refuelEvents, prevRefuelEvents, period]);

  const sortedForRail = useMemo(() => {
    const rows = costPerKmData.length
      ? costPerKmData
      : buses.map((b) => ({
          id: b.id, name: b.name || b.plate, plate: b.plate,
          totalKm: kmByBus[b.id] ?? 0, totalCost: perBusCosts[b.id] ?? 0, costPerKm: null as number | null,
        }));
    const withFigure = buses.map((b) => {
      const row = rows.find((r) => r.id === b.id);
      return {
        id: b.id,
        name: b.name || b.plate,
        plate: b.plate,
        status: b.status,
        totalCost: perBusCosts[b.id] ?? 0,
        totalKm: row?.totalKm ?? kmByBus[b.id] ?? 0,
        costPerKm: row?.costPerKm ?? null,
      };
    });
    const key = (r: typeof withFigure[number]) =>
      railSort === 'cost' ? r.totalCost : railSort === 'perKm' ? (r.costPerKm ?? -1) : r.totalKm;
    return [...withFigure].sort((a, b) => key(b) - key(a));
  }, [buses, costPerKmData, perBusCosts, kmByBus, railSort]);

  const railWithFigures = sortedForRail.filter((r) => r.totalCost > 0);
  const railWithout = sortedForRail.filter((r) => r.totalCost <= 0);

  const selected = useMemo(
    () => sortedForRail.find((r) => r.id === selectedBusId) ?? railWithFigures[0] ?? sortedForRail[0] ?? null,
    [sortedForRail, selectedBusId, railWithFigures],
  );

  const selBus = selected ? buses.find((b) => b.id === selected.id) : undefined;
  const selLitres = selected ? perBusLitres[selected.id] ?? 0 : 0;
  const selEff = selected && selLitres > 0 && selected.totalKm > 0 ? selected.totalKm / selLitres : null;
  const fleetLitres = Object.values(perBusLitres).reduce((s, v) => s + v, 0);
  const fleetEff = fleetLitres > 0 && fleetKm > 0 ? fleetKm / fleetLitres : null;
  const selPrevCost = selected ? prevPerBusCosts[selected.id] ?? 0 : 0;
  const selTrend = selPrevCost > 0 && selected ? ((selected.totalCost - selPrevCost) / selPrevCost) * 100 : null;
  const maxRailCost = Math.max(1, ...sortedForRail.map((r) => r.totalCost));
  const maxPerKm = Math.max(0.0001, ...costPerKmData.map((r) => r.costPerKm ?? 0), fleetCostPerKm ?? 0);

  if (loading) {
    return (
      <div className="fd-screen">
        <header className="fd-appbar">
          <button type="button" className="fd-iconbtn" onClick={onBack} aria-label="Back">
            <FaArrowLeft size={15} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1>Cost estimate</h1>
            <p className="fd-sub">Loading fuel spend…</p>
          </div>
        </header>
        <div className="fd-empty" style={{ paddingTop: 120 }}>
          <b>Loading cost data…</b>
        </div>
      </div>
    );
  }

  const busesWithSpend = railWithFigures.length;

  return (
    <div className="fd-screen">
      <header className="fd-appbar">
        <button type="button" className="fd-iconbtn" onClick={onBack} aria-label="Back">
          <FaArrowLeft size={15} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>Cost estimate</h1>
          <p className="fd-sub">
            Fuel spend per bus · last {period} days
            {busesWithSpend > 0 ? ` · ${busesWithSpend} bus${busesWithSpend === 1 ? '' : 'es'} with recorded refills` : ''}
          </p>
        </div>
        <div className="fd-chips" style={{ margin: 0 }}>
          {([7, 30, 90] as Period[]).map((p) => (
            <button key={p} type="button" className="fd-chip" aria-pressed={period === p} onClick={() => setPeriod(p)}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </header>

      <section className="fd-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="fd-cell">
          <span className="fd-kicker">Fleet fuel spend · {period} days</span>
          <div className="fd-big fd-num">{loadError ? '—' : fmt(fleetTotal, currency)}</div>
          {loadError ? (
            <p className="fd-cellsub" style={{ color: 'var(--fd-crit-700)', fontWeight: 600 }}>{loadError}</p>
          ) : (
            <p className="fd-cellsub">
              {trendPct !== null ? (
                <>
                  <span className={trendPct > 0 ? 'fd-up' : 'fd-down'} style={{ fontWeight: 700 }}>
                    {trendPct > 0 ? '▲' : '▼'} {Math.abs(trendPct).toFixed(1)}%
                  </span>{' '}
                  on the previous {period} days
                </>
              ) : (
                `No previous ${period} days to compare against`
              )}
            </p>
          )}
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Cost per km · fleet</span>
          <div className="fd-big fd-num">{fleetCostPerKm ? fleetCostPerKm.toFixed(1) : '—'}</div>
          <p className="fd-cellsub">
            {fleetCostPerKm
              ? `${currency}/km across ${Math.round(fleetKm).toLocaleString()} km`
              : 'Needs both distance and a logged refuel'}
          </p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Most expensive bus</span>
          <div className="fd-big fd-num">
            {railWithFigures[0]?.costPerKm ? railWithFigures[0].costPerKm.toFixed(1) : '—'}
          </div>
          <p className="fd-cellsub">
            {railWithFigures[0]
              ? `${railWithFigures[0].name} · ${railWithFigures[0].plate}`
              : 'Nothing costed in this period'}
            {railWithFigures[0]?.costPerKm && fleetCostPerKm
              ? ` · ${Math.round(((railWithFigures[0].costPerKm - fleetCostPerKm) / fleetCostPerKm) * 100)}% vs fleet`
              : ''}
          </p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Price basis</span>
          <div className="fd-big" style={{ fontSize: 20 }}>
            <span className="fd-pill fd-pill-warn">Estimate</span>
          </div>
          <p className="fd-cellsub">
            Litres are valued at the price on file, not what the pump charged. A station
            price on Fuel prices makes these exact.
          </p>
        </div>
      </section>

      <div className="fd-split">
        {/* ---------------- rail: buses ranked ---------------- */}
        <aside className="fd-rail">
          <div className="fd-railtop">
            <div className="fd-search">
              <FaSearch style={{ color: 'var(--fd-ink-400)', flex: '0 0 auto' }} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bus or plate"
                aria-label="Search buses"
              />
            </div>
            <div className="fd-chips">
              {([['cost', 'By spend'], ['perKm', 'By cost/km'], ['km', 'By distance']] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="fd-chip"
                  aria-pressed={railSort === key}
                  onClick={() => setRailSort(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fd-raillist">
            {railWithFigures.filter(matchesSearch).length > 0 && (
              <p className="fd-groupname">
                {railSort === 'cost' ? 'Highest spend first' : railSort === 'perKm' ? 'Most expensive per km' : 'Furthest first'}
              </p>
            )}
            {railWithFigures.filter(matchesSearch).map((r) => (
              <button
                key={r.id}
                type="button"
                className="fd-item"
                aria-current={selected?.id === r.id ? 'true' : undefined}
                onClick={() => setSelectedBusId(r.id)}
              >
                <span className={`fd-ig ${r.id === biggestSpenderId ? 'fd-ig-warn' : 'fd-ig-brand'}`}>
                  <FaBus />
                </span>
                <span className="fd-itxt">
                  <span className="fd-ititle" style={{ display: 'block' }}>{r.name}</span>
                  <span className="fd-imeta">
                    <span>{r.plate}</span>
                    <span className="fd-sep">·</span>
                    <span>{fmt(r.totalCost, currency)}</span>
                    {r.costPerKm != null && (
                      <>
                        <span className="fd-sep">·</span>
                        <span>{r.costPerKm.toFixed(1)} /km</span>
                      </>
                    )}
                  </span>
                  <span className="fd-trk" style={{ display: 'block', marginTop: 7 }}>
                    <i
                      className={`fd-fil ${r.id === biggestSpenderId ? 'fd-fil-warn' : ''}`}
                      style={{ width: `${Math.max(4, (r.totalCost / maxRailCost) * 100)}%` }}
                    />
                  </span>
                </span>
              </button>
            ))}

            {railWithout.filter(matchesSearch).length > 0 && (
              <>
                <p className="fd-groupname">No figure available</p>
                {railWithout.filter(matchesSearch).map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="fd-item fd-item-read"
                    aria-current={selected?.id === r.id ? 'true' : undefined}
                    onClick={() => setSelectedBusId(r.id)}
                  >
                    <span className="fd-ig fd-ig-mute"><FaExclamationCircle /></span>
                    <span className="fd-itxt">
                      <span className="fd-ititle" style={{ display: 'block' }}>{r.name}</span>
                      <span className="fd-imeta">
                        <span>{r.plate}</span>
                        <span className="fd-sep">·</span>
                        <span>no refills logged</span>
                      </span>
                    </span>
                  </button>
                ))}
              </>
            )}

            {sortedForRail.filter(matchesSearch).length === 0 && (
              <div className="fd-empty"><b>No buses match</b><span>Try a different search.</span></div>
            )}
          </div>
        </aside>

        {/* ---------------- detail ---------------- */}
        <section className="fd-detail">
          {!selected && (
            <div className="fd-empty" style={{ paddingTop: 120 }}>
              <b>Nothing costed yet</b>
              <span>Once a driver logs a refill with litres, spend appears here.</span>
            </div>
          )}

          {selected && (
            <>
              <div className="fd-hero">
                <div className="fd-heroinner">
                  <div style={{ minWidth: 0 }}>
                    <span className="fd-pill fd-pill-onbrand">
                      {selected.totalCost <= 0
                        ? 'No refills logged in this period'
                        : selected.id === biggestSpenderId
                          ? fleetCostPerKm && selected.costPerKm
                            ? `Most expensive bus · ${Math.round(((selected.costPerKm - fleetCostPerKm) / fleetCostPerKm) * 100)}% above average`
                            : 'Most expensive bus'
                          : 'Fuel spend'}
                    </span>
                    <h2>
                      {selected.totalCost > 0
                        ? `${fmt(selected.totalCost, currency)} on fuel in ${period} days`
                        : `${Math.round(selected.totalKm).toLocaleString()} km driven, nothing recorded going in`}
                    </h2>
                    <div className="fd-herometa">
                      <span className="fd-avatar">{monogram(selected.name)}</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{selected.name}</span>
                      <span className="fd-plate">{selected.plate}</span>
                      <span>·</span>
                      <span>{Math.round(selected.totalKm).toLocaleString()} km</span>
                      {selected.costPerKm != null && (
                        <>
                          <span>·</span>
                          <span>{selected.costPerKm.toFixed(1)} {currency}/km</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="fd-heroactions">
                    <button type="button" className="fd-btn fd-btn-white fd-btn-sm" onClick={() => onDrillDownBus(selected.id)}>
                      Open analytics
                    </button>
                  </div>
                </div>
              </div>

              <div className="fd-tiles">
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Spend · {period} d</span>
                  <p className="fd-tileval fd-num">
                    {selected.totalCost > 0 ? fmt(selected.totalCost, currency) : '—'}
                    {selTrend !== null && (
                      <span className={`fd-tiledelta ${selTrend > 0 ? 'fd-up' : 'fd-down'}`}>
                        {selTrend > 0 ? '▲' : '▼'} {Math.abs(selTrend).toFixed(0)}%
                      </span>
                    )}
                  </p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {perBusRefuelCount[selected.id] ?? 0} refill{(perBusRefuelCount[selected.id] ?? 0) === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Distance</span>
                  <p className="fd-tileval fd-num">{Math.round(selected.totalKm).toLocaleString()} km</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>From finished routes</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Efficiency</span>
                  <p className="fd-tileval fd-num">{selEff ? selEff.toFixed(1) : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {selEff
                      ? `km/L${fleetEff ? ` · fleet ${fleetEff.toFixed(1)}` : ''}`
                      : 'Needs distance and logged litres'}
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Litres · {period} d</span>
                  <p className="fd-tileval fd-num">{selLitres > 0 ? Math.round(selLitres).toLocaleString() : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {perBusLastRefuel[selected.id]
                      ? `Last ${new Date(perBusLastRefuel[selected.id]).toLocaleDateString()}`
                      : 'None recorded'}
                  </p>
                </div>
              </div>

              <div className="fd-grid">
                <div>
                  <div className="fd-card fd-block">
                    <h3>Fleet spend, day by day<span className="fd-rest">Last {period} days</span></h3>
                    {spendCurve.every((d) => d.current === 0 && d.previous === 0) ? (
                      <div className="fd-empty" style={{ padding: '32px 16px' }}>
                        <b>No spend recorded in this window</b>
                        <span>Refills logged with a cost will draw here.</span>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={spendCurve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="fd_spend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#8a63f0" stopOpacity={0.34} />
                              <stop offset="100%" stopColor="#8a63f0" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f4f2fb" vertical={false} />
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9a93b3' }} interval="preserveStartEnd" minTickGap={40} />
                          <YAxis tick={{ fontSize: 11, fill: '#9a93b3' }} tickFormatter={fmtShort} width={48} />
                          <Tooltip
                            formatter={(v: number, n: string) => [fmt(v, currency), n === 'current' ? 'This period' : `Previous ${period} days`]}
                            contentStyle={{ borderRadius: 12, border: '1px solid #e8e4f5', fontSize: 12 }}
                          />
                          <Area type="monotone" dataKey="previous" stroke="#9a93b3" strokeDasharray="5 5" strokeWidth={2} fill="none" />
                          <Area type="monotone" dataKey="current" stroke="#6f4ff0" strokeWidth={2.6} fill="url(#fd_spend)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                    <p className="fd-tiny fd-muted" style={{ margin: '10px 0 0' }}>
                      Running total, so the line only ever climbs. Refills are lumpy — a
                      day-by-day bar would be mostly empty with occasional spikes.
                    </p>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Cost per kilometre, bus by bus<span className="fd-rest">Last {period} days</span></h3>
                    {costPerKmData.length === 0 ? (
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        Nothing has both distance and a logged refill yet, so no bus can be compared.
                      </p>
                    ) : (
                      <div className="fd-bars">
                        {costPerKmData.slice(0, 8).map((r) => (
                          <div className="fd-bar" key={r.id}>
                            <span className="fd-lbl" style={r.id === selected.id ? { color: 'var(--fd-brand-700)' } : undefined}>
                              {r.name}
                            </span>
                            <span className="fd-trk">
                              <i
                                className={`fd-fil ${r.costPerKm == null ? 'fd-fil-mute' : r.id === biggestSpenderId ? 'fd-fil-warn' : 'fd-fil-ok'}`}
                                style={{ width: r.costPerKm == null ? '0%' : `${(r.costPerKm / maxPerKm) * 100}%` }}
                              />
                            </span>
                            <span className="fd-val fd-num">{r.costPerKm == null ? '—' : r.costPerKm.toFixed(1)}</span>
                          </div>
                        ))}
                        {fleetCostPerKm && (
                          <div className="fd-bar">
                            <span className="fd-lbl" style={{ color: 'var(--fd-ink-500)' }}>Fleet average</span>
                            <span className="fd-trk">
                              <i className="fd-fil fd-fil-mute" style={{ width: `${(fleetCostPerKm / maxPerKm) * 100}%` }} />
                            </span>
                            <span className="fd-val fd-num">{fleetCostPerKm.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="fd-tiny fd-muted" style={{ margin: '12px 0 0' }}>
                      A bus with no logged refill shows a dash rather than a zero — a zero here
                      would read as perfect economy.
                    </p>
                  </div>

                  {busesWithSpend > 0 && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>Spend by bus<span className="fd-rest">{busesWithSpend} costed</span></h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={barChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f4f2fb" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9a93b3' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#9a93b3' }} tickFormatter={fmtShort} width={48} />
                          <Tooltip
                            formatter={(v: number) => [fmt(v, currency), 'Spend']}
                            contentStyle={{ borderRadius: 12, border: '1px solid #e8e4f5', fontSize: 12 }}
                          />
                          <Bar dataKey="cost" radius={[6, 6, 0, 0]} onClick={(d: any) => setSelectedBusId(d.busId)}>
                            {barChartData.map((entry, i) => (
                              <Cell key={entry.busId} fill={i === 0 ? '#d97706' : CHART_COLORS[(i + 1) % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div>
                  <div className="fd-card fd-buscard">
                    <div className="fd-bctop">
                      <span className="fd-avatar" style={{ borderColor: 'rgba(255,255,255,.5)' }}>{monogram(selected.name)}</span>
                      <div>
                        <b>{selected.name}</b>
                        <span>{selected.plate}</span>
                      </div>
                    </div>
                    <div className="fd-bcinner">
                      <div className="fd-bcrow"><span>Spend · {period} d</span><span className="fd-num">{selected.totalCost > 0 ? fmt(selected.totalCost, currency) : '—'}</span></div>
                      <div className="fd-bcrow"><span>Distance</span><span className="fd-num">{Math.round(selected.totalKm).toLocaleString()} km</span></div>
                      <div className="fd-bcrow">
                        <span>Cost per km</span>
                        <span className="fd-num" style={selected.id === biggestSpenderId ? { color: 'var(--fd-warn-700)' } : undefined}>
                          {selected.costPerKm != null ? `${selected.costPerKm.toFixed(1)} ${currency}` : '—'}
                        </span>
                      </div>
                      <div className="fd-bcrow"><span>Efficiency</span><span className="fd-num">{selEff ? `${selEff.toFixed(1)} km/L` : '—'}</span></div>
                      <div className="fd-bcrow">
                        <span>Refills</span>
                        <span>
                          {perBusRefuelCount[selected.id] ?? 0}
                          {perBusLastRefuel[selected.id] ? ` · last ${new Date(perBusLastRefuel[selected.id]).toLocaleDateString()}` : ''}
                        </span>
                      </div>
                      <div className="fd-bcrow">
                        <span>Benchmark</span>
                        <span>{selBus?.benchmarkKmPerL ? `${selBus.benchmarkKmPerL} km/L` : <span className="fd-nodata">Not set</span>}</span>
                      </div>
                      <div className="fd-bcrow">
                        <span>Status</span>
                        <span>
                          <span className={`fd-pill ${selBus?.status === 'active' ? 'fd-pill-ok' : 'fd-pill-warn'}`}>
                            <span className={`fd-dot ${selBus?.status === 'active' ? 'fd-dot-ok' : 'fd-dot-warn'}`} />
                            {selBus?.status === 'active' ? 'In service' : selBus?.status === 'for_hire' ? 'For hire' : selBus?.status === 'suspended' ? 'Suspended' : 'Maintenance'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Price basis</h3>
                    <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                      Every figure on this page is litres × the price on file. Live EPRA ceilings
                      cannot be fetched from our servers, so those prices are the corrected
                      regional defaults — a station price you enter yourself beats a national
                      ceiling anyway.
                    </p>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Do next</h3>
                    <button type="button" className="fd-btn fd-btn-primary fd-btn-sm fd-btn-block" onClick={() => onDrillDownBus(selected.id)}>
                      Open bus analytics
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
